import { useMemo } from "react";
import { CustomSettings } from "subModule/src/services/invoice/types";
import { useDefaultLabels } from "./useDefaultLabels";

/**
 * Hook to pre-fill all labels with default translations
 * Returns a function that enriches settings with default labels for all enabled fields
 * This ensures labels are always present in the payload, even if user hasn't customized them
 */
export const usePreFillLabels = () => {
  const {
    getCompanyLabel,
    getCustomerLabel,
    getVehicleLabel,
    getTableColumnLabel,
    getSummaryLabel,
    getPaymentLabel,
  } = useDefaultLabels();

  const preFillLabels = useMemo(() => {
    return (settings: CustomSettings): CustomSettings => {
      const enrichedSettings = { ...settings };

      // Pre-fill company labels for enabled fields
      if (settings.companyFields) {
        enrichedSettings.companyLabels = { ...settings.companyLabels };
        Object.entries(settings.companyFields).forEach(([field, enabled]) => {
          if (enabled) {
            const currentLabels =
              settings.companyLabels?.[
                field as keyof typeof settings.companyLabels
              ];
            const needsPreFill =
              !currentLabels ||
              (!currentLabels.primary && !currentLabels.secondary) ||
              (currentLabels.primary === "" && currentLabels.secondary === "");

            if (needsPreFill) {
              const defaultLabels = getCompanyLabel(field);
              enrichedSettings.companyLabels![
                field as keyof typeof enrichedSettings.companyLabels
              ] = {
                primary: defaultLabels.primary,
                secondary: defaultLabels.secondary,
              };
            }
          }
        });
      }

      // Pre-fill customer labels for enabled fields
      if (settings.customerFields) {
        enrichedSettings.customerLabels = { ...settings.customerLabels };
        Object.entries(settings.customerFields).forEach(([field, enabled]) => {
          if (enabled) {
            const currentLabels =
              settings.customerLabels?.[
                field as keyof typeof settings.customerLabels
              ];
            const needsPreFill =
              !currentLabels ||
              (!currentLabels.primary && !currentLabels.secondary) ||
              (currentLabels.primary === "" && currentLabels.secondary === "");

            if (needsPreFill) {
              const defaultLabels = getCustomerLabel(field);
              enrichedSettings.customerLabels![
                field as keyof typeof enrichedSettings.customerLabels
              ] = {
                primary: defaultLabels.primary,
                secondary: defaultLabels.secondary,
              };
            }
          }
        });
      }

      // Pre-fill vehicle labels for enabled fields
      if (settings.vehicleFields) {
        enrichedSettings.vehicleLabels = { ...settings.vehicleLabels };
        Object.entries(settings.vehicleFields).forEach(([field, enabled]) => {
          if (enabled) {
            const currentLabels =
              settings.vehicleLabels?.[
                field as keyof typeof settings.vehicleLabels
              ];
            const needsPreFill =
              !currentLabels ||
              (!currentLabels.primary && !currentLabels.secondary) ||
              (currentLabels.primary === "" && currentLabels.secondary === "");

            if (needsPreFill) {
              const defaultLabels = getVehicleLabel(field);
              enrichedSettings.vehicleLabels![
                field as keyof typeof enrichedSettings.vehicleLabels
              ] = {
                primary: defaultLabels.primary,
                secondary: defaultLabels.secondary,
              };
            }
          }
        });
      }

      // Pre-fill table column labels for enabled columns
      if (settings.tableColumns) {
        enrichedSettings.tableColumnLabels = { ...settings.tableColumnLabels };
        Object.entries(settings.tableColumns).forEach(([column, enabled]) => {
          if (enabled) {
            const currentLabels =
              settings.tableColumnLabels?.[
                column as keyof typeof settings.tableColumnLabels
              ];
            const needsPreFill =
              !currentLabels ||
              (!currentLabels.primary && !currentLabels.secondary) ||
              (currentLabels.primary === "" && currentLabels.secondary === "");

            if (needsPreFill) {
              const defaultLabels = getTableColumnLabel(column);
              enrichedSettings.tableColumnLabels![
                column as keyof typeof enrichedSettings.tableColumnLabels
              ] = {
                primary: defaultLabels.primary,
                secondary: defaultLabels.secondary,
              };
            }
          }
        });
      }

      // Pre-fill consolidated table column labels for enabled columns
      if (settings.consolidatedTableColumns) {
        enrichedSettings.consolidatedTableColumnLabels = {
          ...settings.consolidatedTableColumnLabels,
        };

        Object.entries(settings.consolidatedTableColumns).forEach(
          ([column, enabled]) => {
            if (enabled) {
              const currentLabels =
                settings.consolidatedTableColumnLabels?.[
                  column as keyof typeof settings.consolidatedTableColumnLabels
                ];

              const needsPreFill =
                !currentLabels ||
                (!currentLabels.primary && !currentLabels.secondary) ||
                (currentLabels.primary === "" &&
                  currentLabels.secondary === "");

              if (needsPreFill) {
                const defaultLabels = getTableColumnLabel(column);

                enrichedSettings.consolidatedTableColumnLabels![
                  column as keyof typeof enrichedSettings.consolidatedTableColumnLabels
                ] = {
                  primary: defaultLabels.primary,
                  secondary: defaultLabels.secondary,
                };
              }
            }
          }
        );
      }

      // Pre-fill summary item labels for enabled items
      if (settings.summaryItems) {
        enrichedSettings.summaryLabels = { ...settings.summaryLabels };
        Object.entries(settings.summaryItems).forEach(([item, enabled]) => {
          if (enabled) {
            const currentLabels =
              settings.summaryLabels?.[
                item as keyof typeof settings.summaryLabels
              ];
            const needsPreFill =
              !currentLabels ||
              (!currentLabels.primary && !currentLabels.secondary) ||
              (currentLabels.primary === "" && currentLabels.secondary === "");

            if (needsPreFill) {
              const defaultLabels = getSummaryLabel(item);
              enrichedSettings.summaryLabels![
                item as keyof typeof enrichedSettings.summaryLabels
              ] = {
                primary: defaultLabels.primary,
                secondary: defaultLabels.secondary,
              };
            }
          }
        });
      }

      // Pre-fill creditAmount, paidAmount, dueAmount labels (always shown if payment info is enabled)
      if (settings.showPaymentInfo) {
        enrichedSettings.summaryLabels = { ...enrichedSettings.summaryLabels };
        ["creditAmount", "paidAmount", "dueAmount"].forEach((item) => {
          const currentLabels =
            enrichedSettings.summaryLabels?.[
              item as keyof typeof enrichedSettings.summaryLabels
            ];
          const needsPreFill =
            !currentLabels ||
            (!currentLabels.primary && !currentLabels.secondary) ||
            (currentLabels.primary === "" && currentLabels.secondary === "");

          if (needsPreFill) {
            const defaultLabels = getSummaryLabel(item);
            enrichedSettings.summaryLabels![
              item as keyof typeof enrichedSettings.summaryLabels
            ] = {
              primary: defaultLabels.primary,
              secondary: defaultLabels.secondary,
            };
          }
        });
      }

      // Pre-fill payment labels if payment info is shown
      if (settings.showPaymentInfo) {
        const currentPaymentLabels = settings.paymentLabels;
        const needsPreFill =
          !currentPaymentLabels ||
          (!currentPaymentLabels.primary && !currentPaymentLabels.secondary) ||
          (currentPaymentLabels.primary === "" &&
            currentPaymentLabels.secondary === "");

        if (needsPreFill) {
          const defaultLabels = getPaymentLabel();
          enrichedSettings.paymentLabels = {
            primary: defaultLabels.primary,
            secondary: defaultLabels.secondary,
          };
        }
      }

      return enrichedSettings;
    };
  }, [
    getCompanyLabel,
    getCustomerLabel,
    getVehicleLabel,
    getTableColumnLabel,
    getSummaryLabel,
    getPaymentLabel,
  ]);

  return { preFillLabels };
};
